import React from 'react';

import { Icon } from '../../../../../components';
import { Trigger, Panel } from './Tooltip.styles';

export type TooltipProps = {
  /** The trigger's accessible name (already translated); the panel is what it describes. */
  label: string;
  /** The panel's content: the caller renders it (a `<Text>` per state) so each string is a literal. */
  children: React.ReactNode;
  /** Pin the trigger, as a bare 18px glyph, to the top-right corner of the caller's positioned box
   *  (the Plan stat card), the panel opening under it; the caller reserves the corner with padding.
   *  Default: in flow, with the round 36px button chrome. */
  corner?: boolean;
  /** Paint the glyph red, for a card whose explanation is bad news, so the eye lands on the card
   *  that explains it. */
  danger?: boolean;
  testId?: string;
};

/**
 * An info button with a hover-and-focus panel, scoped to this scenario (nova/components has no
 * Tooltip, and this one deletes with the folder). Built for more than a mouse: the trigger is a real
 * button (tabbable, Enter and Space toggle it), the panel opens on hover AND on focus, closes on
 * blur, on Escape and on a second activation, and is linked from the trigger with
 * `aria-describedby`, so its copy is announced rather than being pointer-only.
 *
 * POSITIONING: the panel is absolute against the CALLER'S nearest positioned ancestor, right-aligned
 * and capped at that box's width, so it cannot overflow it at 390px. The caller supplies the
 * containing block (the Plan stat card is `position: relative` for this). With `corner` the
 * trigger itself is pinned to that box's top-right corner and the panel opens beneath it.
 */
export const Tooltip = ({
  label,
  children,
  corner = false,
  danger = false,
  testId,
}: TooltipProps): React.ReactElement => {
  const id = React.useId();
  const [open, setOpen] = React.useState(false);
  const show = React.useCallback(() => setOpen(true), []);
  const hide = React.useCallback(() => setOpen(false), []);
  const toggle = React.useCallback(() => setOpen((was) => !was), []);

  // Escape closes a panel opened by hover as well as one opened by focus, so the listener is on the
  // document while open, not only on the trigger.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <Trigger
        type="button"
        $corner={corner}
        $danger={danger}
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={toggle}
        data-testid={testId}
      >
        <Icon name="info" size={18} color="currentColor" />
      </Trigger>
      <Panel id={id} role="tooltip" hidden={!open} $corner={corner}>
        {children}
      </Panel>
    </>
  );
};

export default Tooltip;
